import { useEffect, useCallback } from 'react'
import { useBrickStore, type CameraPreset } from '@store/brickStore'

/**
 * Keyboard Shortcuts Hook
 *
 * Centralized keyboard input handler for the Brick Builder 3D application.
 *
 * Current Shortcuts:
 * - Delete/Backspace: Delete selected brick
 * - R: Rotate preview brick (if showing) OR selected brick by 90 degrees (0 → 90 → 180 → 270 → 0)
 * - Ctrl+Z / Cmd+Z: Undo last action (Task 3.2.4)
 * - Ctrl+Y / Cmd+Y: Redo previously undone action (Task 3.2.4)
 * - Ctrl+Shift+Z / Cmd+Shift+Z: Redo (alternative) (Task 3.2.4)
 * - 1: Isometric camera view (Task 3.2.5)
 * - 2: Top camera view (Task 3.2.5)
 * - 3: Front camera view (Task 3.2.5)
 * - 4: Side camera view (Task 3.2.5)
 *
 * Features:
 * - Prevents shortcuts when typing in input fields
 * - Supports both Windows (Ctrl) and Mac (Cmd) modifiers
 * - Clean event listener management (no memory leaks)
 * - Extensible for future shortcuts
 * - Returns callback for triggering toast notifications
 *
 * @param onShortcutAction - Callback for visual feedback (toast notifications)
 *
 * @example
 * ```tsx
 * const handleShortcutFeedback = (message: string) => {
 *   showToast(message)
 * }
 *
 * useKeyboardShortcuts(handleShortcutFeedback)
 * ```
 */
export const useKeyboardShortcuts = (
  onShortcutAction?: (message: string, type?: 'success' | 'error' | 'info') => void
) => {
  // Store actions
  const selectedBrickId = useBrickStore((state) => state.selectedBrickId)
  const previewPosition = useBrickStore((state) => state.previewPosition)
  const removeBrick = useBrickStore((state) => state.removeBrick)
  const rotateBrick = useBrickStore((state) => state.rotateBrick)
  const rotatePreview = useBrickStore((state) => state.rotatePreview)
  const setSelectedBrickId = useBrickStore((state) => state.setSelectedBrickId)
  const undo = useBrickStore((state) => state.undo)
  const redo = useBrickStore((state) => state.redo)
  const canUndo = useBrickStore((state) => state.canUndo)
  const canRedo = useBrickStore((state) => state.canRedo)
  const setActiveCameraPreset = useBrickStore(
    (state) => state.setActiveCameraPreset
  )

  /**
   * Check if user is typing in an input field
   * Prevents shortcuts from triggering while user is entering text
   */
  const isTyping = useCallback((event: KeyboardEvent): boolean => {
    const target = event.target as HTMLElement
    const tagName = target.tagName.toLowerCase()

    return (
      tagName === 'input' ||
      tagName === 'textarea' ||
      target.isContentEditable
    )
  }, [])

  /**
   * Handle Delete/Backspace key - Delete selected brick
   */
  const handleDelete = useCallback(() => {
    if (!selectedBrickId) {
      onShortcutAction?.(
        'No brick selected. Click a brick to select it first.',
        'info'
      )
      return
    }

    removeBrick(selectedBrickId)
    setSelectedBrickId(null)
    onShortcutAction?.('Brick deleted', 'success')
  }, [selectedBrickId, removeBrick, setSelectedBrickId, onShortcutAction])

  /**
   * Handle R key - Rotate brick or preview
   * Task 3.2.2 - Rotate selected brick (cycles 0 → 90 → 180 → 270 → 0)
   * Task 3.2.8 - Rotate preview brick before placement (cycles 0 → 90 → 180 → 270 → 0)
   *
   * Priority:
   * 1. If preview is showing: rotate preview brick
   * 2. If brick is selected: rotate selected brick
   * 3. Otherwise: show info message
   */
  const handleRotate = useCallback(() => {
    // Priority 1: Rotate preview brick if showing
    if (previewPosition) {
      const newRotation = rotatePreview()
      onShortcutAction?.(`Preview Rotation: ${newRotation}°`, 'success')
      return
    }

    // Priority 2: Rotate selected brick if one is selected
    if (selectedBrickId) {
      const newRotation = rotateBrick(selectedBrickId)
      onShortcutAction?.(`Brick rotated to ${newRotation}°`, 'success')
      return
    }

    // Priority 3: No preview or selection
    onShortcutAction?.(
      'No brick selected. Click a brick to select it first.',
      'info'
    )
  }, [previewPosition, selectedBrickId, rotatePreview, rotateBrick, onShortcutAction])

  /**
   * Handle Ctrl+Z / Cmd+Z - Undo
   * Task 3.2.4 - Undo last action (addBrick, removeBrick, rotateBrick)
   */
  const handleUndo = useCallback(() => {
    if (!canUndo()) {
      onShortcutAction?.('Nothing to undo', 'info')
      return
    }

    const actionDescription = undo()
    if (actionDescription) {
      onShortcutAction?.(`Undone: ${actionDescription}`, 'success')
    } else {
      onShortcutAction?.('Nothing to undo', 'info')
    }
  }, [undo, canUndo, onShortcutAction])

  /**
   * Handle Ctrl+Y / Cmd+Y - Redo
   * Task 3.2.4 - Redo previously undone action
   */
  const handleRedo = useCallback(() => {
    if (!canRedo()) {
      onShortcutAction?.('Nothing to redo', 'info')
      return
    }

    const actionDescription = redo()
    if (actionDescription) {
      onShortcutAction?.(`Redone: ${actionDescription}`, 'success')
    } else {
      onShortcutAction?.('Nothing to redo', 'info')
    }
  }, [redo, canRedo, onShortcutAction])

  /**
   * Handle number keys 1-4 - Camera presets
   * Task 3.2.5 - Switch camera view
   */
  const handleCameraPreset = useCallback(
    (preset: CameraPreset, label: string) => {
      setActiveCameraPreset(preset)
      onShortcutAction?.(`Camera switched to ${label}`, 'success')
    },
    [setActiveCameraPreset, onShortcutAction]
  )

  /**
   * Main keyboard event handler
   * Routes keyboard events to appropriate handlers
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      if (isTyping(event)) {
        return
      }

      const key = event.key.toLowerCase()
      const isCtrlOrCmd = event.ctrlKey || event.metaKey

      // Delete/Backspace - Delete selected brick
      if (key === 'delete' || key === 'backspace') {
        event.preventDefault() // Prevent browser back navigation
        handleDelete()
        return
      }

      // R - Rotate brick (future)
      if (key === 'r' && !isCtrlOrCmd) {
        event.preventDefault()
        handleRotate()
        return
      }

      // Ctrl+Z / Cmd+Z - Undo (future)
      if (key === 'z' && isCtrlOrCmd && !event.shiftKey) {
        event.preventDefault()
        handleUndo()
        return
      }

      // Ctrl+Y / Cmd+Y - Redo (future)
      // Also Ctrl+Shift+Z / Cmd+Shift+Z (alternative redo shortcut)
      if (
        (key === 'y' && isCtrlOrCmd) ||
        (key === 'z' && isCtrlOrCmd && event.shiftKey)
      ) {
        event.preventDefault()
        handleRedo()
        return
      }

      // Camera presets - Number keys 1-4 (Task 3.2.5)
      if (!isCtrlOrCmd) {
        if (key === '1') {
          event.preventDefault()
          handleCameraPreset('isometric', 'Isometric View')
          return
        }
        if (key === '2') {
          event.preventDefault()
          handleCameraPreset('top', 'Top View')
          return
        }
        if (key === '3') {
          event.preventDefault()
          handleCameraPreset('front', 'Front View')
          return
        }
        if (key === '4') {
          event.preventDefault()
          handleCameraPreset('side', 'Side View')
          return
        }
      }

      // TODO: Add Escape key to deselect brick
    },
    [isTyping, handleDelete, handleRotate, handleUndo, handleRedo, handleCameraPreset]
  )

  /**
   * Set up keyboard event listener
   * Cleanup on unmount to prevent memory leaks
   */
  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])
}
